/**
 * src/engine/useEngine.js
 *
 * Main engine hook. Mount in TenantApp after useAppState.
 *
 * Usage:
 *   const engine = useEngine({ state, updateState, tenantId, session });
 *   // engine.runJobManually(jobId)
 *   // engine.jobs
 *   // engine.loading
 *   // engine.error      — non-null when the job list could not be loaded
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { onEngineEvent } from './engineEventBus.js';
import { runJob } from './runJob.js';
import { runIdentityAccessSweep, isIdentitySweepEligible } from './identityAccessSweep.js';
import { loadJobsForTenant } from '../api/engineApi.js';
import { isScheduleDue } from './jobRegistry.js';
import { now } from '../utils/formatting.js';

const POLL_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

export function useEngine({ state, updateState, tenantId, session, users }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Non-null when the job list could not be fetched. An engine job is a
  // scheduled control: if the list fails to load, the tab must say so rather
  // than render an empty list, which reads to an admin as "this tenant has no
  // custom jobs" — a claim the product cannot make when it never got an
  // answer (#5580).
  const [error, setError] = useState(null);
  const jobsRef = useRef([]);
  const stateRef = useRef(state);

  // Keep stateRef current without causing re-renders
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // ── Load jobs ─────────────────────────────────────────────────────────────
  // Gate on `session` so we don't fire an unauthenticated request before the
  // JWT is restored from localStorage / set by login.
  useEffect(() => {
    if (!tenantId || !session) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadJobsForTenant(tenantId)
      .then((loaded) => {
        if (cancelled) return;
        setJobs(loaded);
        jobsRef.current = loaded;
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        console.error('[useEngine] failed to load jobs:', err);
        if (cancelled) return;
        setError('Could not load the engine jobs for this tenant.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, session]);

  // ── Helper: run a single job ──────────────────────────────────────────────
  const execute = useCallback(
    (job, triggeredBy, eventPayload) => {
      return runJob(job, {
        state: stateRef.current,
        updateState,
        tenantId,
        session,
        triggeredBy,
        eventPayload,
      });
    },
    [updateState, tenantId, session]
  );

  // Keep a ref to the latest `execute` so schedule sweeps don't need it as an
  // effect dependency — `execute` is recreated whenever `session` is swapped
  // (e.g. a sliding-session refresh), which must not re-trigger the sweep.
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  }, [execute]);

  // Per-job last-run timestamps, updated synchronously the moment a job is
  // fired so a re-entrant sweep (mount effect + hourly poll racing, or a
  // sweep re-run before jobsRef.current has been reloaded from the server)
  // can't see a stale lastRunAt and re-execute the same job within the hour.
  const lastRunAtRef = useRef({});

  const runDueJobs = useCallback(() => {
    const schedulable = jobsRef.current.filter(
      (j) => j.enabled && (j.trigger.type === 'schedule' || j.trigger.type === 'condition')
    );
    for (const job of schedulable) {
      const schedule = job.trigger.schedule || 'daily';
      const lastRunAt = lastRunAtRef.current[job.id] ?? job.lastRunAt;
      if (isScheduleDue(schedule, lastRunAt)) {
        lastRunAtRef.current[job.id] = now();
        executeRef.current(job, job.trigger.type === 'condition' ? 'condition' : 'schedule');
      }
    }
  }, []);

  // ── On mount: run schedule + condition jobs that are due ──────────────────
  // Deps are intentionally narrowed to primitives (tenantId, presence of a
  // session, loading) so a session *identity* change alone — without
  // tenantId/loading actually changing — doesn't re-run the sweep.
  const hasSession = !!session;
  useEffect(() => {
    if (!tenantId || !hasSession || loading) return;
    runDueJobs();
  }, [loading, tenantId, hasSession, runDueJobs]);

  // ── Hourly poll: re-check schedule jobs ───────────────────────────────────
  useEffect(() => {
    if (!tenantId || !hasSession) return;
    const timer = setInterval(runDueJobs, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [tenantId, hasSession, runDueJobs]);

  // ── Identity access sweep (IDNA-03) ───────────────────────────────────────
  // A built-in derived process rather than a tenant-configured job: comparing
  // required access against held access is not something a tenant opts into,
  // and there is nothing to configure. Same mount + hourly cadence as the job
  // sweep above, and administrator-only — see identityAccessSweep.js for why.
  const usersRef = useRef(users);
  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const identityEligible = isIdentitySweepEligible(session);
  const runIdentitySweep = useCallback(() => {
    if (!tenantId || !identityEligible) return;
    runIdentityAccessSweep({
      state: stateRef.current,
      updateState,
      users: usersRef.current,
      session: sessionRef.current,
      tenantId,
    }).catch((err) => console.error('[useEngine] identity access sweep failed:', err));
  }, [tenantId, identityEligible, updateState]);

  useEffect(() => {
    if (!tenantId || !identityEligible || loading) return;
    runIdentitySweep();
    const timer = setInterval(runIdentitySweep, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [tenantId, identityEligible, loading, runIdentitySweep]);

  // ── Event listeners ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!tenantId || !session || loading) return;

    const EVENT_TYPES = [
      'document.saved',
      'document.deleted',
      'task.saved',
      'task.evidence_added',
      'risk.saved',
      'vendor.saved',
      'vendor.subprocessors_changed',
    ];

    const unsubs = EVENT_TYPES.map((eventName) =>
      onEngineEvent(eventName, (payload) => {
        const matching = jobsRef.current.filter(
          (j) => j.enabled && j.trigger.type === 'event' && j.trigger.event === eventName
        );
        for (const job of matching) {
          execute(job, 'event', payload);
        }

        // No SDLC in-effect write-back here any more. It used to run on every
        // `task.saved` and persist `treatmentSetup.inEffect`; that flag is gone
        // and in-effect is derived on read by `isRiskSdlcInEffect`, so a task
        // change needs no risk write at all.
      })
    );

    return () => unsubs.forEach((fn) => fn());
  }, [loading, tenantId, session, execute]);

  // ── Manual run (called from UI) ───────────────────────────────────────────
  const runJobManually = useCallback(
    async (jobId) => {
      const job = jobsRef.current.find((j) => j.id === jobId);
      if (!job) return null;
      return execute(job, 'manual');
    },
    [execute]
  );

  // ── Reload jobs (after create/edit/delete) ────────────────────────────────
  // Reloaded after a create/edit/delete. A failure here is reported the same
  // way as the initial load: the previously-loaded list is left in place (it is
  // the last state we actually know) and the error is surfaced, so a silently
  // stale list can never be mistaken for a confirmed one.
  const reloadJobs = useCallback(() => {
    if (!tenantId) return Promise.resolve();
    return loadJobsForTenant(tenantId)
      .then((loaded) => {
        setJobs(loaded);
        jobsRef.current = loaded;
        setError(null);
      })
      .catch((err) => {
        console.error('[useEngine] failed to reload jobs:', err);
        setError('Could not refresh the engine jobs — the list below may be out of date.');
      });
  }, [tenantId]);

  return { jobs, loading, error, runJobManually, reloadJobs };
}
